import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { resetPassword } from "../../api/auth";
import { ApiError } from "../../api/http";
import { Button, PasswordInput, Label } from "../../components/ui";
import { useT } from "../../i18n";
import { AuthLayout } from "./AuthLayout";

export function ResetPasswordPage() {
  const t = useT();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error.generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={t("auth.reset.title")}>
      {done ? (
        <p role="status" className="text-sm text-success">{t("auth.reset.done")}</p>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label htmlFor="password">{t("auth.reset.password")}</Label>
            <PasswordInput
              id="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              showLabel={t("common.showPassword")}
              hideLabel={t("common.hidePassword")}
            />
          </div>
          {error && <p role="alert" className="text-sm text-danger">{error}</p>}
          <Button type="submit" loading={busy} className="w-full">
            {t("auth.reset.submit")}
          </Button>
        </form>
      )}
      <p className="mt-4 text-center">
        <Link to="/login" className="text-sm font-medium text-brand hover:underline">
          {t("auth.backToLogin")}
        </Link>
      </p>
    </AuthLayout>
  );
}
